/**
 * Aseli Store Locator — third-party retailer finder.
 *
 * Idempotent (data-sl-bound guard) + shopify:section:load support for the editor.
 * Renderer = Leaflet/OSM, loaded lazily (only when the section scrolls into view)
 * to keep Core Web Vitals stable — no map scripts/styles hit the page until then.
 *
 * Data source is decoupled: it comes from a <script type="application/json"
 * data-store-locator-data> blob (dummy retailers now; Metaobject loop in Phase 4).
 *
 * Search is client-side for now: matches the query against the retailer list
 * (zip prefix → city contains) and uses the first match as the origin, then
 * Haversine-filters/sorts by radius. Geolocation uses the browser API.
 * (Free-text geocoding of arbitrary places can be layered on later via Nominatim.)
 */
(function () {
  var EARTH_KM = 6371;

  function haversine(a, b) {
    var toRad = function (d) { return (d * Math.PI) / 180; };
    var dLat = toRad(b.lat - a.lat);
    var dLng = toRad(b.lng - a.lng);
    var s =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * EARTH_KM * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /**
   * Custom marker icon — the real Aseli mouse (assets/images/logo/aseli-favicon.svg,
   * served as theme asset aseli-marker.svg). Loaded via <img> so the SVG's
   * clipPath id doesn't collide across the many marker instances on the map.
   * iconAnchor sits at the lower centre so the mouse "stands" on the location.
   */
  function mouseIcon(config) {
    var url = config && config.markerIcon ? config.markerIcon : '';
    var img = url
      ? '<img class="sl-marker-img" src="' + url + '" width="40" height="40" alt="" draggable="false">'
      : '';
    return window.L.divIcon({
      html: '<div class="sl-marker">' + img + '</div>',
      className: 'sl-marker-wrap',
      iconSize: [40, 40],
      iconAnchor: [20, 33],
      popupAnchor: [0, -30]
    });
  }

  /**
   * Custom cluster icon — a clean red Aseli pill with the store count.
   * Replaces Leaflet's default (calendar-like) cluster styling.
   */
  function clusterIcon(cluster) {
    var count = cluster.getChildCount();
    return window.L.divIcon({
      html: '<div class="sl-cluster"><span>' + count + '</span></div>',
      className: 'sl-cluster-wrap',
      iconSize: [44, 44]
    });
  }

  /**
   * Geocode an arbitrary place/PLZ query via OpenStreetMap Nominatim.
   * Lets the radius search work for inputs that aren't themselves a store
   * (e.g. a PLZ with no store nearby). Scoped to Germany. For heavy traffic a
   * dedicated geocoder/proxy should be layered in front of Nominatim.
   */
  function geocode(query, cb) {
    var url = 'https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&countrycodes=de&q=' +
      encodeURIComponent(query);
    fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && data[0] && data[0].lat && data[0].lon) {
          cb(parseFloat(data[0].lat), parseFloat(data[0].lon));
        } else {
          cb(null, null);
        }
      })
      .catch(function () { cb(null, null); });
  }

  function loadLeaflet(config, cb) {
    // CSS first (no onload needed).
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = config.leafletCss;
    document.head.appendChild(link);
    var link2 = document.createElement('link');
    link2.rel = 'stylesheet';
    link2.href = config.clusterCss;
    document.head.appendChild(link2);

    var onReady = function () { if (window.L && window.L.markerClusterGroup) cb(); };

    if (window.L && window.L.markerClusterGroup) { cb(); return; }
    var s = document.createElement('script');
    s.src = config.leafletJs;
    s.onload = function () {
      if (window.L && window.L.markerClusterGroup) { cb(); return; }
      var s2 = document.createElement('script');
      s2.src = config.clusterJs;
      s2.onload = onReady;
      document.head.appendChild(s2);
    };
    document.head.appendChild(s);
  }

  function initSection(root) {
    if (!root || root.getAttribute('data-sl-bound')) return;
    root.setAttribute('data-sl-bound', '1');

    var config = {};
    var configEl = root.querySelector('[data-store-locator-config]');
    try { config = JSON.parse(configEl.textContent.trim()) || {}; } catch (e) { config = {}; }
    var maxList = config.maxList || 50;

    var stores = [];
    var dataEl = root.querySelector('[data-store-locator-data]');
    try { stores = JSON.parse(dataEl.textContent.trim()) || []; } catch (e) { stores = []; }
    var byId = {};
    stores.forEach(function (s) { byId[s.id] = s; });

    var listEl = root.querySelector('[data-sl-list]');
    var mapEl = root.querySelector('[data-sl-map]');
    var inputEl = root.querySelector('[data-sl-input]');
    var radiusEl = root.querySelector('[data-sl-radius]');
    var geoEl = root.querySelector('[data-sl-geo]');
    var infoEl = root.querySelector('[data-sl-info]');

    if (!listEl || !mapEl || !stores.length) return;

    var state = {
      origin: null,        // {lat,lng} of search/geolocation; null = show all
      filtered: stores.slice(),
      map: null,
      cluster: null,
      markers: {},         // id -> leaflet marker
      listItemById: {},
      activeId: null,
      mapStarted: false
    };

    function applyFilter() {
      // Read radius straight from the select (single source of truth) so a
      // changed dropdown always takes effect immediately.
      var radius = radiusEl ? radiusEl.value : '';
      if (state.origin) {
        state.filtered = stores.slice()
          .map(function (s) { return { s: s, d: haversine(state.origin, s) }; })
          .filter(function (o) { return !radius || o.d <= parseFloat(radius); })
          .sort(function (a, b) { return a.d - b.d; })
          .map(function (o) { return o.s; });
      } else {
        state.filtered = stores.slice().sort(function (a, b) {
          return String(a.zip || '').localeCompare(String(b.zip || ''));
        });
      }
      renderList();
      renderMarkers();
      updateInfo();
    }

    // UI copy is read from data-sl-tpl-* attributes on the section root (rendered via
    // locale strings in aseli-store-locator.liquid), so texts follow the storefront
    // language. German literals below are last-resort fallbacks only.
    function tpl(name, fallback) {
      var v = root.getAttribute('data-sl-tpl-' + name);
      return v != null && v !== '' ? v : fallback;
    }

    function fill(text, vars) {
      return String(text).replace(/\{(\w+)\}/g, function (m, key) {
        return vars[key] != null ? vars[key] : m;
      });
    }

    function updateInfo() {
      var n = state.filtered.length;
      var shown = Math.min(n, maxList);
      var scope = state.origin ? tpl('scope-nearby', 'im Umkreis') : tpl('scope-all', 'in Deutschland');
      var txt = fill(n === 1 ? tpl('count-one', '{n} Geschäft {scope}') : tpl('count-many', '{n} Geschäfte {scope}'), { n: n, scope: scope });
      if (n > shown) txt += fill(tpl('show-next', ' — zeige die {n} nächsten'), { n: shown });
      infoEl.textContent = txt;
    }

    function renderList() {
      listEl.innerHTML = '';
      state.listItemById = {};
      var shown = state.filtered.slice(0, maxList);

      if (!shown.length) {
        listEl.innerHTML = '<p class="store-locator__empty">' + tpl('empty-radius', 'Keine Geschäfte in diesem Umkreis — versuche einen größeren Radius oder eine andere PLZ.') + '</p>';
        return;
      }

      shown.forEach(function (s) {
        var item = document.createElement('div');
        item.className = 'store-locator__item';
        item.setAttribute('role', 'listitem');
        item.setAttribute('tabindex', '0');
        item.setAttribute('data-sl-item', s.id);
        var dist = state.origin ? haversine(state.origin, s) : null;
        item.innerHTML = listItemHTML(s, dist);
        item.addEventListener('click', function () { focusStore(s.id); });
        item.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); focusStore(s.id); }
        });
        listEl.appendChild(item);
        state.listItemById[s.id] = item;
      });
      reflectActive();
    }

    function listItemHTML(s, dist) {
      var p = [];
      p.push('<div class="store-locator__item-head">');
      p.push('<h3 class="store-locator__item-name">' + esc(s.name) + '</h3>');
      if (dist !== null) p.push('<span class="store-locator__item-dist">' + dist.toFixed(1) + ' km</span>');
      p.push('</div>');
      if (s.street || s.zip || s.city) {
        p.push('<p class="store-locator__item-addr">');
        if (s.street) p.push(esc(s.street) + '<br>');
        p.push((s.zip ? esc(s.zip) + ' ' : '') + esc(s.city || ''));
        p.push('</p>');
      }
      if (s.hours) p.push('<p class="store-locator__item-hours">' + esc(s.hours).replace(/\n/g, '<br>') + '</p>');
      var links = [];
      if (s.phone) links.push('<a class="store-locator__item-link" href="tel:' + esc(String(s.phone).replace(/[^0-9+]/g, '')) + '">Anrufen</a>');
      if (s.website) links.push('<a class="store-locator__item-link" href="' + esc(s.website) + '" target="_blank" rel="noopener">Website</a>');
      links.push('<a class="store-locator__item-link" href="https://www.openstreetmap.org/directions?to=' + s.lat + '%2C' + s.lng + '" target="_blank" rel="noopener">Route</a>');
      p.push('<div class="store-locator__item-links">' + links.join('') + '</div>');
      return p.join('');
    }

    function popupHTML(s) {
      var h = '<div class="sl-popup"><strong>' + esc(s.name) + '</strong><br>';
      if (s.street) h += esc(s.street) + '<br>';
      if (s.zip || s.city) h += (s.zip ? esc(s.zip) + ' ' : '') + esc(s.city || '') + '<br>';
      if (s.hours) h += '<small>' + esc(s.hours).replace(/\n/g, '<br>') + '</small><br>';
      h += '<a href="https://www.openstreetmap.org/directions?to=' + s.lat + '%2C' + s.lng + '" target="_blank" rel="noopener">Route planen</a></div>';
      return h;
    }

    function renderMarkers() {
      if (!state.cluster) return; // map not ready yet — markers added on startMap()
      state.cluster.clearLayers();
      state.markers = {};
      state.filtered.forEach(function (s) {
        var m = window.L.marker([s.lat, s.lng], { icon: mouseIcon(config) }).bindPopup(popupHTML(s));
        m.on('click', function () { setActive(s.id, false); });
        state.cluster.addLayer(m);
        state.markers[s.id] = m;
      });
    }

    function focusStore(id) {
      setActive(id, true);
      var s = byId[id];
      if (!s || !state.map || !state.markers[id]) return;
      var marker = state.markers[id];
      state.map.setView([s.lat, s.lng], Math.max(state.map.getZoom(), 13), { animate: true });
      // Spiderfy out of a cluster if needed, then open the popup.
      state.cluster.zoomToShowLayer(marker, function () { marker.openPopup(); });
    }

    function setActive(id, scrollList) {
      state.activeId = id;
      reflectActive();
      if (scrollList && state.listItemById[id]) {
        state.listItemById[id].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    function reflectActive() {
      Object.keys(state.listItemById).forEach(function (k) {
        state.listItemById[k].classList.toggle('is-active', String(k) === String(state.activeId));
      });
    }

    function startMap() {
      if (state.mapStarted) return;
      state.mapStarted = true;
      loadLeaflet(config, function () {
        var L = window.L;
        state.map = L.map(mapEl, { scrollWheelZoom: false }).setView(
          [config.initialLat, config.initialLng],
          config.initialZoom
        );
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        }).addTo(state.map);
        // Enable scroll-zoom only while the user is actively interacting (keeps page scroll smooth).
        state.map.on('focus', function () { state.map.scrollWheelZoom.enable(); });
        state.map.on('blur', function () { state.map.scrollWheelZoom.disable(); });

        state.cluster = L.markerClusterGroup({
          showCoverageOnHover: false,
          maxClusterRadius: 50,
          iconCreateFunction: clusterIcon
        });
        state.map.addLayer(state.cluster);
        renderMarkers();
        setTimeout(function () { if (state.map) state.map.invalidateSize(); }, 200);
      });
    }

    var geoSeq = 0;

    function setOrigin(lat, lng) {
      state.origin = { lat: lat, lng: lng };
      applyFilter();
      if (state.map) state.map.setView([lat, lng], Math.max(state.map.getZoom(), 11));
    }

    // ---- Search: instant client-side match, else geocode the query ----
    function runSearch() {
      var q = (inputEl.value || '').trim();
      if (!q) {
        state.origin = null;
        applyFilter();
        return;
      }
      var ql = q.toLowerCase();
      // 1) instant match against known stores (no network)
      var match = null;
      for (var i = 0; i < stores.length; i++) {
        if (stores[i].zip && stores[i].zip.toLowerCase().indexOf(ql) === 0) { match = stores[i]; break; }
      }
      if (!match) {
        for (var j = 0; j < stores.length; j++) {
          if (stores[j].city && stores[j].city.toLowerCase().indexOf(ql) !== -1) { match = stores[j]; break; }
        }
      }
      if (match) {
        setOrigin(match.lat, match.lng);
        return;
      }
      // 2) no local match → geocode the arbitrary query (PLZ/ort with no store)
      //    so the radius filter reveals nearby stores in other places.
      var seq = ++geoSeq;
      infoEl.textContent = fill(tpl('searching', 'Suche nach „{q}" …'), { q: q });
      geocode(q, function (lat, lng) {
        if (seq !== geoSeq) return; // superseded by a newer keystroke
        if (lat == null) {
          infoEl.textContent = fill(tpl('no-match', 'Kein Treffer für „{q}" — tippe eine PLZ oder einen Ort.'), { q: q });
          return;
        }
        setOrigin(lat, lng);
      });
    }

    var searchTimer = null;
    inputEl.addEventListener('input', function () {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(runSearch, 500);
    });
    inputEl.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); clearTimeout(searchTimer); runSearch(); }
    });

    radiusEl.addEventListener('change', applyFilter);

    geoEl.addEventListener('click', function () {
      if (!navigator.geolocation) { infoEl.textContent = tpl('geo-unsupported', 'Standortbestimmung wird von diesem Browser nicht unterstützt.'); return; }
      geoEl.setAttribute('disabled', 'disabled');
      navigator.geolocation.getCurrentPosition(function (pos) {
        geoEl.removeAttribute('disabled');
        state.origin = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        inputEl.value = '';
        applyFilter();
        if (state.map) state.map.setView([state.origin.lat, state.origin.lng], Math.max(state.map.getZoom(), 11));
      }, function () {
        geoEl.removeAttribute('disabled');
        infoEl.textContent = 'Standort konnte nicht ermittelt werden — bitte PLZ oder Ort eingeben.';
      }, { timeout: 8000, enableHighAccuracy: false });
    });

    // Initial list render (works before the map is loaded).
    applyFilter();

    // Lazy-load the map only when the section approaches the viewport.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) { startMap(); io.disconnect(); }
        });
      }, { rootMargin: '300px' });
      io.observe(mapEl);
    } else {
      startMap();
    }
  }

  function initAll() {
    document.querySelectorAll('[data-store-locator]').forEach(initSection);
  }

  if (document.readyState !== 'loading') {
    initAll();
  } else {
    document.addEventListener('DOMContentLoaded', initAll);
  }

  // Theme editor: re-init the freshly loaded section.
  document.addEventListener('shopify:section:load', function (event) {
    var node = event.target && event.target.querySelector && event.target.querySelector('[data-store-locator]');
    if (node) initSection(node);
  });
})();
